import React from 'react';
import { Clock, CheckCircle, XCircle, Calendar, FileText, Book } from 'lucide-react';
import type { BetaAssignment } from '../../../types';

interface AssignmentListProps {
  assignments: BetaAssignment[];
  isAuthor?: boolean;
  onSelect: (assignment: BetaAssignment) => void;
}

export default function AssignmentList({
  assignments,
  isAuthor,
  onSelect
}: AssignmentListProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <XCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      default:
        return 'Not Started';
    }
  };

  return (
    <div className="space-y-4">
      {assignments.map((assignment) => (
        <div
          key={assignment.id}
          className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => onSelect(assignment)}
        >
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-medium text-gray-900">{assignment.title}</h3>
                {assignment.description && (
                  <p className="text-sm text-gray-600 mt-1">
                    {assignment.description}
                  </p>
                )}
              </div>
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                assignment.status === 'completed'
                  ? 'bg-green-100 text-green-800'
                  : assignment.status === 'in_progress'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {getStatusIcon(assignment.status)}
                <span>{getStatusText(assignment.status)}</span>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
              {assignment.chapter_id ? (
                <div className="flex items-center gap-1">
                  <Book className="w-4 h-4" />
                  <span>Chapter Assignment</span>
                </div>
              ) : assignment.document_url ? (
                <div className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  <span>Document Review</span>
                </div>
              ) : null}
              
              {assignment.due_date && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Due {new Date(assignment.due_date).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {assignments.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isAuthor ? 'No Assignments Created' : 'No Assignments Yet'}
          </h3>
          <p className="text-gray-600">
            {isAuthor
              ? 'Create assignments for your beta readers to get started'
              : 'Check back later for new assignments from the author'}
          </p>
        </div>
      )}
    </div>
  );
}